import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { RegisterBanksSelect } from '@/modules/common/components/RegisterBanksSelect.tsx';
import { useRegisterClassListInfinityQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterBanksListQuery } from '@/modules/issuer-dashboard/queries/banks.ts';
import { useIssuerGenerateSubscriptionPdfFormMutation } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  SelectPaginate,
  SelectPaginateOption,
} from 'ui';
import { downloadFile, openInNewTab } from 'ui/utils';
import { BankTypeEnum, RegisterBankExtended } from 'api';

interface RegisterClassSelectPaginate {
  id: number;
  class_name: string;
}

interface IssuerSubscriptionPdfFormModalProps extends BaseModalProps {
  registerId: number;
  registerName?: string;
  holderEntityId?: number;
  holderName?: string;
}

export const IssuerSubscriptionPdfFormModal = ({
  open,
  onClose,
  registerId,
  registerName,
  holderEntityId,
  holderName,
}: IssuerSubscriptionPdfFormModalProps) => {
  const [registerClass, setRegisterClass] =
    useState<SelectPaginateOption<RegisterClassSelectPaginate>>();
  const [bank, setBank] = useState<SelectPaginateOption<RegisterBankExtended> | null>(null);
  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery({
    page: 1,
    page_size: 15,
    register_id: registerId,
  });
  const { data: registryBanksData, isFetching: isFetchingRegistryBanks } =
    useRegisterBanksListQuery(
      {
        page: 1,
        page_size: 1,
        registerId,
      },
      !open || !registerId,
    );
  const hasNoRegistryBanks =
    !!registryBanksData &&
    !isFetchingRegistryBanks &&
    (registryBanksData.total || registryBanksData.data?.length || 0) === 0;

  const subscriptionPdfFileName = useMemo(() => {
    const baseName = holderName?.trim() || 'subscription';
    return `${baseName}-subscription-document-${dayjs().format('YYYY-MM-DD-HH-mm')}.pdf`;
  }, [holderName]);

  const { mutate: generateSubscriptionPdfForm, isPending } =
    useIssuerGenerateSubscriptionPdfFormMutation({
      onSuccess: file => {
        if (!file) return;

        const namedFile = new File([file], subscriptionPdfFileName, {
          type: file.type || 'application/pdf',
        });
        const fileUrl = URL.createObjectURL(namedFile);
        openInNewTab(fileUrl);
        window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
        downloadFile(file, subscriptionPdfFileName);
        onClose();
      },
    });

  const handleAsyncRegisterClassList = useCallback(
    async (currentPage: number, search?: string) => {
      const classesData = await fetchNextRegisterClassListPage({
        pageParam: { page: currentPage, search },
      });

      const options: SelectPaginateOption<RegisterClassSelectPaginate>[] =
        classesData?.data?.pages?.at(-1)?.data.map(item => {
          return {
            label: item.class_name,
            value: {
              id: item.id,
              class_name: item.class_name,
            },
          };
        }) || [];

      return options;
    },
    [fetchNextRegisterClassListPage],
  );

  const handleChangeRegisterClass = useCallback(
    (option: SelectPaginateOption<RegisterClassSelectPaginate>) => {
      setRegisterClass(option);
      setBank(null);
    },
    [],
  );

  const handleChangeBank = useCallback(
    (option: SelectPaginateOption<RegisterBankExtended> | null) => {
      setBank(option);
    },
    [],
  );

  const handleGenerate = useCallback(() => {
    if (!registerClass?.value?.id || !bank?.value?.id) return;

    generateSubscriptionPdfForm({
      registerId,
      register_class_id: registerClass.value.id,
      bank_id: bank.value.id,
      holder_entity_id: holderEntityId,
      filename: subscriptionPdfFileName,
    });
  }, [
    bank?.value?.id,
    generateSubscriptionPdfForm,
    holderEntityId,
    registerClass?.value?.id,
    registerId,
    subscriptionPdfFileName,
  ]);

  const handleClose = () => {
    setRegisterClass(undefined);
    setBank(null);
    onClose();
  };

  return (
    <ModalContentWrapper open={open} title="Download Subscription Document" onClose={handleClose}>
      <div className="flex w-full flex-col gap-8 pb-2">
        <p className="text-sm leading-[22px] text-gray-600">
          Select the class and bank details to include in the subscription document.
        </p>

        <div className="flex w-full items-start gap-6">
          <div className="flex w-full flex-col">
            <span className="mb-2 font-[Inter] text-[10px] uppercase leading-[18px] text-gray-900 opacity-64">
              REGISTER
            </span>
            <div className="min-h-[38px] border-b border-gray-900 border-opacity-16 py-1 text-sm leading-[30px] text-gray-900">
              {registerName || `Register #${registerId}`}
            </div>
          </div>

          <SelectPaginate<RegisterClassSelectPaginate>
            key={registerId}
            onAsync={handleAsyncRegisterClassList}
            label="CLASS"
            onChange={handleChangeRegisterClass}
            value={registerClass}
            placeholder="Select..."
          />
        </div>

        <RegisterBanksSelect
          key={registerClass?.value?.id || 'no-class'}
          label="BANK"
          placeholder="Select..."
          registerId={registerId}
          value={bank?.value?.id}
          onChange={handleChangeBank}
          bankType={BankTypeEnum.Subscription}
          needBankType={BankTypeEnum.Subscription}
          needClassId={registerClass?.value?.id}
          warning={
            hasNoRegistryBanks
              ? 'This registry has no bank. Add one before generating this report.'
              : undefined
          }
        />

        <Button
          onClick={handleGenerate}
          loading={isPending}
          disabled={isPending || !registerClass?.value?.id || !bank?.value?.id}
        >
          Download
        </Button>
      </div>
    </ModalContentWrapper>
  );
};
